import {
  computeSafeViewportRect,
  rectIntersectsSafeViewport,
} from '../../../../shared/onboarding/missionViewport';
import { isTourTargetInDom } from './waitForTarget';

export { isTourTargetInDom };

export function isTourTargetInViewport(
  element: Element,
  minRatio = 0.5,
  safeRect = computeSafeViewportRect(),
): boolean {
  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 && rect.height <= 0) {
    return false;
  }
  return rectIntersectsSafeViewport(rect, safeRect, minRatio);
}

export function isTourTargetInteractable(element: Element): boolean {
  if (!(element instanceof HTMLElement)) {
    return false;
  }
  if (element.hasAttribute('disabled') || element.getAttribute('aria-disabled') === 'true') {
    return false;
  }
  const style = window.getComputedStyle(element);
  if (style.pointerEvents === 'none' || style.visibility === 'hidden') {
    return false;
  }
  if (element.closest('[aria-hidden="true"]')) {
    return false;
  }
  return true;
}

export function isTourTargetUnoccluded(
  element: Element,
  excludeSelectors: string[] = ['[data-tour-stable="learning-lab-mission-panel"]'],
): boolean {
  if (!(element instanceof HTMLElement)) {
    return false;
  }
  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) {
    return false;
  }

  const isExcludedHit = (hit: Element | null): boolean => {
    if (!hit) {
      return false;
    }
    for (const selector of excludeSelectors) {
      const excluded = document.querySelector(selector);
      if (excluded && (excluded === hit || excluded.contains(hit))) {
        return true;
      }
    }
    return false;
  };

  const isTargetHit = (hit: Element | null): boolean =>
    Boolean(hit && (element === hit || element.contains(hit)));

  const centerX = rect.left + rect.width / 2;
  const centerY = rect.top + rect.height / 2;
  const centerHit = document.elementFromPoint(centerX, centerY);

  if (isTargetHit(centerHit)) {
    return true;
  }
  if (isExcludedHit(centerHit)) {
    return false;
  }

  // Adjacent UI at corners should not false-positive occlusion; only the mission panel blocks.
  return true;
}

export function isTourTargetMissionReady(
  element: Element,
  options: {
    minVisibleRatio?: number;
    excludeSelectors?: string[];
  } = {},
): boolean {
  if (!isTourTargetInDom(element)) {
    return false;
  }
  const style = window.getComputedStyle(element);
  if (
    style.display === 'none' ||
    style.visibility === 'hidden' ||
    Number.parseFloat(style.opacity) === 0
  ) {
    return false;
  }
  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 && rect.height <= 0) {
    return false;
  }
  const safe = computeSafeViewportRect();
  const minRatio = options.minVisibleRatio ?? 0.5;
  if (!isTourTargetInViewport(element, minRatio, safe)) {
    return false;
  }
  if (!isTourTargetInteractable(element)) {
    return false;
  }
  return isTourTargetUnoccluded(element, options.excludeSelectors);
}

export type MissionTargetVisibilityState = 'ready' | 'missing' | 'occluded';

export function classifyMissionTargetVisibility(
  element: Element | null,
): MissionTargetVisibilityState {
  if (!element || !isTourTargetInDom(element)) {
    return 'missing';
  }
  const style = window.getComputedStyle(element);
  if (
    style.display === 'none' ||
    style.visibility === 'hidden' ||
    Number.parseFloat(style.opacity) === 0
  ) {
    return 'missing';
  }
  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 && rect.height <= 0) {
    return 'missing';
  }
  if (!isTourTargetMissionReady(element)) {
    return 'occluded';
  }
  return 'ready';
}
